import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BrandLogo } from '../components/BrandLogo';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { startCheckout } from '../lib/checkout';

const tiers = [
  {
    name: 'Player / Parent', priceMonthly: 9, priceAnnual: 90, saveAnnual: 18, tierMonth: 'player_month', tierYear: 'player_year',
    desc: 'For players and families who want a clearer picture of development.',
    features: ['Personal development profile', 'Access to coach feedback', 'Tagged video moments', 'Progress tracking', 'Evaluation history', 'Private player/family view'],
    cta: 'Get Started',
  },
  {
    name: 'Coach / Advisor', priceMonthly: 19, priceAnnual: 190, saveAnnual: 38, tierMonth: 'coach_month', tierYear: 'coach_year',
    desc: 'For coaches, advisors and development professionals working directly with players.',
    features: ['Up to 25 athletes', 'Unlimited evaluations', 'Video moment tags', '10-category development rubric', 'Progress tracking', 'Player/parent access', 'CSV & printable reports'],
    cta: 'Get Started',
  },
  {
    name: 'Team / Camp', priceMonthly: 29, priceAnnual: 290, saveAnnual: 58, tierMonth: 'team_month', tierYear: 'team_year',
    desc: 'For teams, camps and group development programs.',
    features: ['Up to 50 athletes', 'Multiple coaches', 'Unlimited evaluations', 'Video moment tags', 'Player & family access', 'Team development tracking', 'Reports & evaluations', 'Priority email support'],
    cta: 'Get Started', highlighted: true,
  },
  {
    name: 'Academy', priceMonthly: null, priceAnnual: null,
    desc: 'For multi-team programs, academies and organizations.',
    features: ['Unlimited coaches', 'Unlimited athletes', 'Multiple teams & programs', 'Custom onboarding', 'Organization-wide development tracking', 'Advanced reporting', 'Dedicated support'],
    cta: 'Talk to Us',
  },
];

const faqs = [
  { q: 'Do I need a credit card to explore?', a: 'You can browse ShiftNotes and see the full workflow before choosing a plan. Payment is only required when you activate an athlete on a paid tier.' },
  { q: 'Can I switch plans later?', a: 'Absolutely. Upgrade or downgrade at any time from your account settings; changes prorate automatically.' },
  { q: 'How does annual pricing work?', a: 'Choose annual and you effectively get two months free. No long-term contract  cancel monthly plans anytime.' },
  { q: 'Who built ShiftNotes?', a: 'ShiftNotes is built by NextShift Development LLC  led by a coach with 25+ years of NCAA and Professional hockey experience  in collaboration with development-minded programs.' },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const buy = (tier) => {
    if (!tier.priceMonthly) { window.location.href = 'mailto:rob@shiftnotes.io?subject=ShiftNotes%20Academy%20Inquiry'; return; }
    startCheckout(annual ? tier.tierYear : tier.tierMonth, { user, navigate, toast });
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')}>
            <BrandLogo variant="inline" />
          </button>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-600 hover:text-[#0b1f4d]">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back home
            </Button>
            <Button onClick={() => document.getElementById('tiers')?.scrollIntoView({ behavior: 'smooth' })} className="bg-[#1e40af] hover:bg-[#1d4ed8] text-white font-semibold">Get Started</Button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-40" />
        <div className="relative max-w-5xl mx-auto px-6 pt-16 pb-8 text-center">
          <Badge className="bg-sky-500/10 text-sky-700 border-sky-500/30 mb-5">Built for Every Step of the Development Journey</Badge>
          <h1 className="font-display text-4xl md:text-5xl font-bold">Whether You're a Player, Coach, Team or Academy, ShiftNotes Fits Your Game.</h1>
          <p className="mt-4 text-slate-600 text-lg max-w-2xl mx-auto">
            Simple, affordable pricing designed to make player development accessible to everyone. Choose the plan that fits your development journey. Upgrade or change your plan anytime.
          </p>

          <div className="mt-8 inline-flex items-center bg-white border border-slate-200 rounded-full p-1 shadow-sm">
            <button onClick={() => setAnnual(false)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${!annual ? 'bg-sky-500 text-white' : 'text-slate-500 hover:text-slate-900'}`}>
              Monthly
            </button>
            <button onClick={() => setAnnual(true)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${annual ? 'bg-sky-500 text-white' : 'text-slate-500 hover:text-slate-900'}`}>
              Annual <span className={`text-[10px] px-1.5 py-0.5 rounded ${annual ? 'bg-white/25 text-white' : 'bg-emerald-500/20 text-emerald-700'}`}>Save 2 months</span>
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div id="tiers" className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
          {tiers.map(t => {
            const priceLine = annual
              ? (t.priceAnnual ? { big: `$${t.priceAnnual}`, unit: '/ year' } : { big: 'Custom', unit: '' })
              : (t.priceMonthly ? { big: `$${t.priceMonthly}`, unit: '/ month' } : { big: 'Custom', unit: '' });
            return (
              <div key={t.name} className={`relative rounded-2xl p-7 border bg-white ${t.highlighted ? 'border-sky-500 shadow-lg ring-1 ring-sky-500/20' : 'border-slate-200'}`}>
                {t.highlighted && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sky-500 text-white border-sky-500 font-semibold">Most Popular</Badge>}
                <div className="font-display text-xl font-semibold">{t.name}</div>
                <p className="text-sm text-slate-600 mt-2 min-h-[56px]">{t.desc}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-bold">{priceLine.big}</span>
                  {priceLine.unit && <span className="text-slate-500">{priceLine.unit}</span>}
                </div>
                {t.priceMonthly && (
                  <div className="text-xs text-slate-500 mt-1">
                    {annual ? `= about $${Math.round(t.priceAnnual / 12)} / month` : `or $${t.priceAnnual} / year`}
                  </div>
                )}
                <Button onClick={() => buy(t)}
                  className={`w-full mt-6 font-semibold ${t.highlighted ? 'bg-sky-500 hover:bg-sky-600 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}>
                  {t.cta}
                </Button>
                {t.saveAnnual && <div className="text-xs text-emerald-700 text-center mt-3">Save ${t.saveAnnual} with annual billing.</div>}
                <ul className="mt-6 space-y-2.5 text-sm">
                  {t.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-sky-600 mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-14">
          <h3 className="font-display text-2xl font-semibold">Simple Pricing. Powerful Development.</h3>
          <p className="text-slate-600 mt-2">Pay monthly or save 2 months when you pay annually. No long-term contracts. Cancel monthly plans anytime.</p>
        </div>

        <div className="mt-16">
          <h2 className="font-display text-2xl font-bold text-center mb-8">Common questions</h2>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {faqs.map(f => (
              <div key={f.q} className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="font-semibold">{f.q}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <div>ShiftNotes<sup className="text-[9px] text-sky-600">&trade;</sup>  a product of NextShift Development LLC</div>
          <div> {new Date().getFullYear()} NextShift Development LLC. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
